import React, { Suspense, useCallback, useEffect, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import { AdminColumnPicker, ColumnDef, useColumnPrefs } from '../components/AdminColumnPicker';
import '../components/AdminLayout.css';

// The mailer module's one-by-one send button, loaded only when this page is opened.
const SendMailButton = React.lazy(() => import('../mailer/SendMailButton'));
const LastEmailedCell = React.lazy(() => import('../mailer/LastEmailedCell'));

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAGE_SIZE = 50;

interface WaitlistEntry {
  id: string;
  waitlistNumber: number | null;
  email: string;
  phoneCountryCode: string | null;
  phone: string | null;
  audience: string | null;
  sourceVideoLength: string | null;
  videosPerWeek: string | null;
  status: string;
  referrer: string | null;
  utm: Record<string, string>;
  ip: string | null;
  userAgent: string | null;
  confirmationSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The tracking details are hidden until an admin turns them on with the gear above the table. */
const COLUMNS: ColumnDef[] = [
  { id: 'number', label: '#' },
  { id: 'email', label: 'Email', locked: true },
  { id: 'phone', label: 'Phone' },
  { id: 'audience', label: 'Audience' },
  { id: 'videos', label: 'Videos/wk' },
  { id: 'length', label: 'Source length' },
  { id: 'status', label: 'Status' },
  { id: 'confirmation', label: 'Confirmation sent', defaultVisible: false },
  { id: 'referrer', label: 'Referrer', defaultVisible: false },
  { id: 'utm', label: 'UTM', defaultVisible: false },
  { id: 'ip', label: 'IP', defaultVisible: false },
  { id: 'agent', label: 'User agent', defaultVisible: false },
  { id: 'created', label: 'Signed up' },
  { id: 'updated', label: 'Updated' },
  { id: 'lastEmailed', label: 'Last emailed' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'invalid', label: 'Invalid address' },
];
const AUDIENCE_OPTIONS = [
  { value: '', label: 'All audiences' },
  { value: 'podcaster', label: 'Podcaster' },
  { value: 'high_volume', label: 'High volume' },
  { value: 'agency', label: 'Agency' },
];

const ELLIPSIS: React.CSSProperties = {
  maxWidth: 220,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');
const fmtUtm = (utm: Record<string, string>) => {
  const parts = Object.entries(utm || {});
  return parts.length ? parts.map(([k, v]) => `${k.replace('utm_', '')}=${v}`).join(', ') : '—';
};

const AdminWaitlist: React.FC = () => {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('');
  const [page, setPage] = useState(0);
  const columnPrefs = useColumnPrefs('admin.columns.waitlist', COLUMNS);
  const show = columnPrefs.isVisible;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(page * PAGE_SIZE),
    });
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (audience) params.set('audience', audience);
    fetch(`${API_BASE}/api/admin/waitlist?${params}`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error('request failed');
        return r.json();
      })
      .then(data => {
        setEntries(data.entries || []);
        setTotal(data.total || 0);
      })
      .catch(() => setError('Failed to load waitlist'))
      .finally(() => setLoading(false));
  }, [page, search, status, audience]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back">
        <div className="admin-page-title-row">
          <AdminBackButton />
          <h1 className="admin-page-title">Waitlist Users</h1>
        </div>
        <p className="admin-page-subtitle admin-page-subtitle--data">{total} total signups</p>
      </div>

      <form onSubmit={submitSearch} className="admin-form-inline">
        <input
          className="admin-input admin-input-search"
          placeholder="Search by email or phone…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <select
          className="admin-input admin-input-sm"
          value={status}
          onChange={e => { setPage(0); setStatus(e.target.value); }}
        >
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          className="admin-input admin-input-sm"
          value={audience}
          onChange={e => { setPage(0); setAudience(e.target.value); }}
        >
          {AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button className="admin-btn admin-btn-secondary" type="submit">Search</button>
      </form>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="admin-table-toolbar">
            <AdminColumnPicker columns={COLUMNS} prefs={columnPrefs} />
          </div>
          <div className="admin-card" style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  {show('number') && <th>#</th>}
                  <th>Email</th>
                  {show('phone') && <th>Phone</th>}
                  {show('audience') && <th>Audience</th>}
                  {show('videos') && <th>Videos/wk</th>}
                  {show('length') && <th>Source length</th>}
                  {show('status') && <th>Status</th>}
                  {show('confirmation') && <th>Confirmation sent</th>}
                  {show('referrer') && <th>Referrer</th>}
                  {show('utm') && <th>UTM</th>}
                  {show('ip') && <th>IP</th>}
                  {show('agent') && <th>User agent</th>}
                  {show('created') && <th>Signed up</th>}
                  {show('updated') && <th>Updated</th>}
                  {show('lastEmailed') && <th>Last emailed</th>}
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={columnPrefs.count + 1} className="admin-cell-empty">No signups found.</td></tr>
                )}
                {entries.map(e => (
                  <tr key={e.id}>
                    {show('number') && <td className="admin-cell-small">{e.waitlistNumber ?? '—'}</td>}
                    <td>{e.email}</td>
                    {show('phone') && (
                      <td className="admin-cell-small">
                        {e.phone ? `${e.phoneCountryCode ?? ''} ${e.phone}`.trim() : '—'}
                      </td>
                    )}
                    {show('audience') && <td className="admin-cell-small">{e.audience ?? '—'}</td>}
                    {show('videos') && <td className="admin-cell-small">{e.videosPerWeek ?? '—'}</td>}
                    {show('length') && <td className="admin-cell-small">{e.sourceVideoLength ?? '—'}</td>}
                    {show('status') && (
                      <td>
                        <span className={`admin-badge ${e.status === 'subscribed' ? 'admin-badge-active' : 'admin-badge-archived'}`}>
                          {e.status}
                        </span>
                      </td>
                    )}
                    {show('confirmation') && <td className="admin-cell-muted">{fmtDate(e.confirmationSentAt)}</td>}
                    {show('referrer') && (
                      <td className="admin-cell-muted" style={ELLIPSIS} title={e.referrer ?? ''}>
                        {e.referrer ?? '—'}
                      </td>
                    )}
                    {show('utm') && (
                      <td className="admin-cell-muted" style={ELLIPSIS} title={fmtUtm(e.utm)}>
                        {fmtUtm(e.utm)}
                      </td>
                    )}
                    {show('ip') && <td className="admin-cell-small">{e.ip ?? '—'}</td>}
                    {show('agent') && (
                      <td className="admin-cell-muted" style={ELLIPSIS} title={e.userAgent ?? ''}>
                        {e.userAgent ?? '—'}
                      </td>
                    )}
                    {show('created') && <td className="admin-cell-muted">{fmtDate(e.createdAt)}</td>}
                    {show('updated') && <td className="admin-cell-muted">{fmtDate(e.updatedAt)}</td>}
                    {show('lastEmailed') && (
                      <td className="admin-cell-small">
                        <Suspense fallback="…"><LastEmailedCell source="waitlist" email={e.email} /></Suspense>
                      </td>
                    )}
                    <td>
                      <Suspense fallback={null}>
                        <SendMailButton source="waitlist" id={e.id} email={e.email} />
                      </Suspense>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-form-inline" style={{ marginTop: 16, alignItems: 'center' }}>
            <button
              className="admin-btn admin-btn-secondary"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span className="admin-cell-muted">Page {page + 1} of {pageCount}</span>
            <button
              className="admin-btn admin-btn-secondary"
              disabled={page >= pageCount - 1}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminWaitlist;
